import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import advancedFormat from "dayjs/plugin/advancedFormat"
import localizedFormat from "dayjs/plugin/localizedFormat"
import customParseFormat from "dayjs/plugin/customParseFormat"
import duration from "dayjs/plugin/duration"
import { roundToTwoDecimals } from "@coast/utils"
import { DEFAULT_TIMEZONE } from "../config"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.extend(localizedFormat)
dayjs.extend(duration)
dayjs.extend(customParseFormat)

const LONG_DATETIME_FORMAT = "MMMM D, YYYY, h:mma z"
const DATE_FORMAT = "YYYY-MM-DD"
const TIME_FORMAT = "h:mma z"

export const dateAndTimeToTimestamp = (dateObject, timeObject) =>
  dateObject
    .set("hour", timeObject.hour())
    .set("minute", timeObject.minute())
    .valueOf()

export const millisecondsToTimeString = (ms, includeSeconds = false) => {
  // Returns something like "30 hours, 1 minute"
  const seconds = Math.floor((ms / 1000) % 60)
  const minutes = Math.floor((ms / (1000 * 60)) % 60)
  const hours = Math.floor(ms / (1000 * 60 * 60))

  const addS = (num) => (num === 1 ? "" : "s")

  let retString = ""
  if (hours > 0) {
    retString = `${hours} hour${addS(hours)}, `
  }
  retString += `${minutes} minute${addS(minutes)}`
  if (includeSeconds) {
    retString += `, ${seconds} second${addS(seconds)}`
  }
  return retString
}

export const minutesToHHMM = (minutes) => {
  if ((minutes ?? undefined) === undefined) {
    return ""
  }

  const totalMinutes = Math.round(minutes)
  let hours = Math.floor(totalMinutes / 60)
  let minsRem = totalMinutes % 60

  // Pad with leading zeros
  if (hours < 10) {
    hours = `0${hours}`
  }
  if (minsRem < 10) {
    minsRem = `0${minsRem}`
  }

  return `${hours}:${minsRem}`
}

export const getDurationMinutes = (startTime, endTime) =>
  (endTime - startTime) / (60 * 1000)

export const getDaysBetween = (startDate, endDate) => {
  const dates = []
  let startDateCopy = startDate
  while (startDateCopy.isBefore(endDate.add(1, "day"))) {
    dates.push(startDateCopy)
    startDateCopy = startDateCopy.add(1, "day")
  }
  return dates
}

// format = arr or string
// https://day.js.org/docs/en/parse/string-format#list-of-all-available-parsing-tokens
export const isValidDate = (date, format = ["x", "YYYY-MM-DD"]) => {
  if (!date) return false
  // manual check for 1970-01-01 default date
  const dayjsDate = dayjs(date).utc().format("YYYY-MM-DD")
  if (dayjsDate === "1970-01-01") return false
  // strict validation see https://day.js.org/docs/en/parse/is-valid
  return dayjs(date, format, true).isValid()
}

export const formatDate = (timestamp, timeZone, format) => {
  /**
   * When we display a date on the site, we should use this to ensure
   * we're showing it in the correct timezone.
   *
   * @param {number} timestamp - unix epoch timestamp in milliseconds
   * @param {string} timeZone - one of ['local', 'America/Los_Angeles', ...]
   *    if "local" we use the current user's timezone
   * @param {string} format - one of ['long', 'short', 'date', 'time', arbitrary]
   *    if "arbitrary" it's something like "HH:ss"
   */
  let formatStr = format
  if (format === "long") {
    formatStr = LONG_DATETIME_FORMAT
  } else if (format === "date") {
    formatStr = DATE_FORMAT
  } else if (format === "time") {
    formatStr = TIME_FORMAT
  }

  if (timeZone === "local") {
    return dayjs(timestamp).tz(dayjs.tz.guess()).format(formatStr)
  }

  const timeZoneOrDefault = timeZone || DEFAULT_TIMEZONE

  return dayjs(timestamp).tz(timeZoneOrDefault).format(formatStr)
}

// array of timestamps, returns array of days ['monday', 'tuesday']
export const getDaysOfTheWeek = (dates) => {
  const days = dates?.map((date) => dayjs(date).day())
  return days
    ?.filter((item, index) => days.indexOf(item) === index)
    .map((day) => `${dayjs().day(day).format("dddd")}`)
}

export const daysSinceCreation = (timestamp) => {
  const timestampDate = dayjs(timestamp)
  const currentDate = dayjs().startOf("day")
  return dayjs.duration(currentDate.diff(timestampDate)).asDays()
}

export const calculateAge = (dob) => {
  if (!isValidDate(dob)) return 0
  // Use UTC so we don't inadvertently change the date.
  const dobTz = dayjs.utc(dob)
  return roundToTwoDecimals(dayjs.utc().diff(dobTz, "year", true))
}

export const dateNumberToString = (date) => {
  if (!date) return ""
  return dayjs.utc(date).format(DATE_FORMAT)
}

export const ageToYearsAndMonths = (minimumAge) => {
  const ageInYears = Math.floor(minimumAge)
  const ageInMonths = Math.floor((minimumAge - ageInYears) * 12)
  if (ageInMonths === 0) {
    return `${ageInYears} years`
  }
  return `${ageInYears} years and ${ageInMonths} months`
}

export const ageToAgeGroup = (studentAge) => {
  if (studentAge < 15) {
    return "Under 15 years old"
  }
  if (studentAge >= 15 && studentAge < 17.5) {
    return "Between 15 and 17.5 years old"
  }
  if (studentAge >= 17.5 && studentAge < 18) {
    return "Between 17.5 and 18 years old"
  }
  if (studentAge >= 18 && studentAge < 55) {
    return "18 years old or over"
  }
  if (studentAge >= 55) {
    return "55 years old or over"
  }
  return "Age not specified"
}
